import type { ValidStorageValue } from '@/managers/Storage';
import Logger from '@/utils/logger';

const logger = new Logger({ caller: 'managers.Storage.indexedDBWrapper' });

type UseStore = <T>(
  txMode: 'readonly' | 'readwrite' | 'versionchange',
  callback: (store: IDBObjectStore) => T | PromiseLike<T>,
) => Promise<T>;

export const availableIDBStores = {
  gameStates: {
    name: 'gameSaveStates',
  },
  keyVal: {
    name: 'keyValStore',
  },
};

// Super useful utility for working with IndexedDB.
// Swiped from https://github.com/jakearchibald/idb-keyval/blob/main/src/index.ts
function promisifyRequest<T = undefined>(
  request: IDBRequest<T> | IDBTransaction,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    // @ts-expect-error - oncomplete works on IDBTransaction and onsuccess works for IDBRequest
    request.oncomplete = request.onsuccess = () => resolve(request.result);
    // @ts-expect-error - onabort works on IDBTransaction and onerror works for IDBRequest
    request.onabort = request.onerror = () => reject(request.error);
  });
}

const idbWrapper = {
  dbName: 'pbskdb',

  dbVersion: 1,

  validateVersionNum: () => {
    if (idbWrapper.dbVersion % 1 !== 0) {
      throw new Error(`Invalid dbVersion: ${idbWrapper.dbVersion}. IndexedDB version MUST be an integer. IDB converts floats to the closest lower integer.`);
    }
  },

  openDB: (dbName: string): Promise<IDBDatabase> => {
    idbWrapper.validateVersionNum();

    const request = indexedDB.open(dbName, idbWrapper.dbVersion);
    const openPromise = promisifyRequest(request);

    request.onupgradeneeded = () => {
      request.result.createObjectStore(availableIDBStores.gameStates.name);
      request.result.createObjectStore(availableIDBStores.keyVal.name);
    };

    openPromise
      .then((db) => {
        db.addEventListener('close', () => logger.debug(`Database ${dbName} closed`));
        db.addEventListener('versionchange', (event) =>
          logger.debug(`Database ${dbName} version changed`, event));
      })
      .catch((ev) => logger.debug('Caught error: ', ev));

    return openPromise;
  },

  getStore: (dbName: string, storeName: string): UseStore => {
    const req = idbWrapper.openDB(dbName);

    return (txMode, callback) =>
      req.then((db) =>
        callback(db.transaction(storeName, txMode).objectStore(storeName)),
      );
  },

  get: (key: string, storeName: string): Promise<ValidStorageValue> => {
    const store = idbWrapper.getStore(idbWrapper.dbName, storeName);
    return store('readonly', (store) => promisifyRequest(store.get(key)));
  },

  set: (key: string, value: ValidStorageValue, storeName: string): Promise<void> => {
    const store = idbWrapper.getStore(idbWrapper.dbName, storeName);

    return store('readwrite', (store) => {
      store.put(value, key);
      return promisifyRequest(store.transaction);
    });
  },

  remove: (key: string, storeName: string): Promise<void> => {
    const store = idbWrapper.getStore(idbWrapper.dbName, storeName);

    return store('readwrite', (store) => {
      store.delete(key);
      return promisifyRequest(store.transaction);
    });
  },
};

const idbKeyValWrapper = {
  defaultStore: availableIDBStores.keyVal.name,

  get: async (key: string, store?: string): Promise<ValidStorageValue|null> => {
    const storeName = store || idbKeyValWrapper.defaultStore;
    return await idbWrapper.get(key, storeName) || null;
  },

  set: (key: string, value: ValidStorageValue, store?: string): void => {
    const storeName = store || idbKeyValWrapper.defaultStore;
    idbWrapper.set(key, value, storeName)
      .catch((err) => logger.warn(`idbKeyValWrapper.set failed for key ${key}: `, err));
  },

  remove: (key: string, store?: string): void => {
    const storeName = store || idbKeyValWrapper.defaultStore;
    idbWrapper.remove(key, storeName)
      .catch((err) => logger.warn(`idbKeyValWrapper.remove failed for key ${key} in store ${storeName}: `, err));
  },
};

export {
  idbWrapper,
  idbKeyValWrapper,
};
